import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useMutation } from '@apollo/client'
import { useRouter } from 'next/router'
import { ExternalLink } from '@/atoms/ExternalLink'
import { ArrowLeftIcon } from '@/atoms/Icons/ArrowLeftIcon'
import { WarningCircleIcon } from '@/atoms/Icons/WarningCircleIcon'
import { InternalLink } from '@/atoms/InternalLink'
import { CaptionLG, EyebrowSM, ParagraphSM, TitleLG, TitleMD } from '@/atoms/Text'
import { paths } from '@/constants'
import {
  CollectibleCartItem,
  useCollectiblesPurchaseContext,
} from '@/contexts/CollectiblesPurchaseContext/CollectiblesPurchaseContextProvider'
import {
  CANCEL_PAYMENT_INTENT,
  CREATE_COLLECTIBLES_PRODUCT_PAYMENT_INTENT,
  UPDATE_PRODUCT_PAYMENT_INTENT,
} from '@/services/NftService'
import {
  CollectibleProductPaymentIntent,
  CollectibleProductResponse,
  CollectibleProductUpdatePaymentIntent,
} from '@/services/NftService/types'
import { getPriceForCurrency } from '@/services/PriceFormatter'
import { CancelPaymentIntentInput } from '@/types/codegen-federation'
import { Product, useEcommerceEvents } from '@/utils/analytics/ecommerce-events'
import { reportErrorToBugsnag, reportWarningToBugsnag } from '@/utils/bugsnag'
import { Translate, useTranslate } from '@/utils/translate/translate-client'
import AddressSection from '@/views/NFTPurchaseView/AddressSection'
import PackOrderSummary from '@/views/NFTPurchaseView/PackOrderSummary'
import PurchaseSection from '@/views/NFTPurchaseView/PurchaseSection'
import { MomentsPurchaseDescription } from '../NFTPurchaseView/MomentsPurchaseDescription'
import { PacksPurchaseDescription } from '../NFTPurchaseView/PacksPurchaseDescription'

function PaymentInformation(props: { paymentType: string; paymentDescription: string }) {
  const { t } = useTranslate('account')

  return (
    <div className="flex flex-col gap-y-2">
      <TitleMD weight="semibold">{t('paymentInformation', 'Payment Information')}:</TitleMD>
      <ParagraphSM>{props.paymentDescription}</ParagraphSM>
    </div>
  )
}

export const CollectiblesCheckoutView: FC = () => {
  const { t } = useTranslate('account')
  const { momentsInCart, cartItems, packsInCart } = useCollectiblesPurchaseContext()
  const [hasError, setHasError] = useState(false)
  const moments = momentsInCart
  const packs = packsInCart
  const selectedMoments = useMemo(() => cartItems, [cartItems])
  const { query } = useRouter()
  const projectId = query?.projectId as string
  const colRef = useRef<HTMLDivElement>(null)
  const { trackCheckoutStarted } = useEcommerceEvents()

  const hasSignatureProductOnly = query?.signature === 'true'
  const hasKeepsakeProductOnly = query?.keepsake === 'true'

  const [checkoutState, setCheckoutState] = useState<'ADDRESS' | 'PAYMENT'>('ADDRESS')

  const changeAddressCheckoutState = useCallback(() => setCheckoutState('ADDRESS'), [])
  const changePaymentCheckoutState = useCallback(() => setCheckoutState('PAYMENT'), [])

  const [getPaymentIntent, { data: paymentIntentData, loading: paymentIntentLoading, called: paymentIntentCalled }] =
    useMutation<CollectibleProductPaymentIntent>(CREATE_COLLECTIBLES_PRODUCT_PAYMENT_INTENT, {
      errorPolicy: 'all',
    })

  const [updatePaymentIntentAmount, { data: updatedPaymentIntentData, loading: updatedPaymentIntentLoading }] =
    useMutation<CollectibleProductUpdatePaymentIntent>(UPDATE_PRODUCT_PAYMENT_INTENT, {
      errorPolicy: 'all',
    })

  const [cancelPaymentIntent, { data: canceledPaymentIntentData }] = useMutation<CancelPaymentIntentInput>(
    CANCEL_PAYMENT_INTENT,
    {
      errorPolicy: 'all',
    },
  )

  useEffect(() => {
    colRef?.current?.scrollIntoView({ behavior: 'smooth' })
  }, [])

  // const cancelOnExit = useCallback(() => {
  //   const paymentIntentId = paymentIntentData?.collectiblesCreateProductPaymentIntent?.node?.spiid
  //   if (paymentIntentId) {
  //     cancelPaymentIntent({
  //       variables: {
  //         input: { paymentIntentId },
  //       },
  //     }).catch((error) => {
  //       reportErrorToBugsnag(`Error cancelling payment intent on exit: ${error}`)
  //     })
  //   }
  // }, [cancelPaymentIntent, paymentIntentData])

  // useEffect(() => {
  //   const handleBeforeUnload = (event: BeforeUnloadEvent) => {
  //     event.preventDefault()
  //     event.returnValue = ''
  //     cancelOnExit()
  //   }

  //   window.addEventListener('beforeunload', handleBeforeUnload)
  //   router.events.on('routeChangeStart', cancelOnExit)

  //   return () => {
  //     window.removeEventListener('beforeunload', handleBeforeUnload)
  //     router.events.off('routeChangeStart', cancelOnExit)
  //   }
  // }, [router.events, cancelOnExit])

  useEffect(() => {
    const paymentIntentId = paymentIntentData?.collectiblesCreateProductPaymentIntent?.node?.spiid

    let inactivityTimeout = setTimeout(() => {
      cancelPaymentIntent({
        variables: {
          input: {
            paymentIntentId,
          },
        },
      }).catch((error) => {
        reportErrorToBugsnag(`Error cancelling payment intent after inactivity: ${error}`)
      })
    }, 900000) // 15 minutes timer

    const resetInactivityTimeout = () => {
      clearTimeout(inactivityTimeout)
      inactivityTimeout = setTimeout(() => {
        cancelPaymentIntent({
          variables: {
            input: {
              paymentIntentId,
            },
          },
        }).catch((error) => {
          reportErrorToBugsnag(`Error cancelling payment intent after inactivity: ${error}`)
        })
      }, 900000) // 15 minutes timer
    }

    window.addEventListener('mousemove', resetInactivityTimeout)
    window.addEventListener('keydown', resetInactivityTimeout)

    return () => {
      window.removeEventListener('mousemove', resetInactivityTimeout)
      window.removeEventListener('keydown', resetInactivityTimeout)
      clearTimeout(inactivityTimeout)
    }
  }, [cancelPaymentIntent, paymentIntentData])

  useEffect(() => {
    setHasError(false)
    getPaymentIntent({
      variables: {
        offers: selectedMoments.map((item) => {
          const { total, signerImage, signerName, shippingSize, shippingTier, ...cartItem } = item
          const products = cartItem.products.map((product) => {
            const { __typename, price, ...rest } = product
            return rest
          })
          return { ...cartItem, products }
        }),
      },
    })
      .then(() => {
        setHasError(false)
      })
      .catch((error) => {
        reportWarningToBugsnag(error)
        setTimeout(() => {
          setHasError(true)
        }, 3000)
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMoments])

  const handleUpdatePaymentIntentAmount = useCallback(() => {
    if (!paymentIntentData?.collectiblesCreateProductPaymentIntent?.node?.spiid) return

    updatePaymentIntentAmount({
      variables: {
        offers: selectedMoments.map((item) => {
          return {
            paymentIntentId: paymentIntentData?.collectiblesCreateProductPaymentIntent?.node?.spiid,
            offerId: item.offerId,
            products: item.products.map((product) => {
              const { price, ...rest } = product
              return rest
            }),
          }
        }),
      },
    })
  }, [
    paymentIntentData?.collectiblesCreateProductPaymentIntent?.node?.spiid,
    updatePaymentIntentAmount,
    selectedMoments,
  ])

  const combinedPaymentIntentData = useMemo(
    () =>
      ({
        ...paymentIntentData?.collectiblesCreateProductPaymentIntent,
        ...updatedPaymentIntentData?.collectiblesUpdateProductPaymentIntent,
      } as CollectibleProductResponse),
    [paymentIntentData, updatedPaymentIntentData],
  )

  useEffect(() => {
    const paymentIntent = combinedPaymentIntentData.node
    if (!paymentIntent) return

    const products: Product[] = []
    packsInCart.forEach((pack) => {
      products.push({
        category: 'collectibles',
        name: pack.name,
        price: getPriceForCurrency(pack.price, 'USD'),
        currency: 'USD',
        quantity: 1,
        product_id: pack.productId,
        sku: pack.productId,
      })
    })
    momentsInCart.forEach((moment) => {
      products.push({
        category: 'collectibles',
        name: moment.title,
        price: getPriceForCurrency(
          cartItems.find((item) => item.offerId === moment.defaultOffer?.id)?.total ?? 0,
          'USD',
        ),
        currency: 'USD',
        quantity: 1,
        product_id: moment.id,
        sku: moment.id,
      })
    })

    trackCheckoutStarted({
      currency: 'USD',
      funnel: 'collectibles',
      checkout_id: paymentIntent.order?.id,
      value: paymentIntent.price ? getPriceForCurrency(paymentIntent.price, 'USD') : 0,
      products,
      projectSlug: projectId,
      tax: paymentIntent.tax ? getPriceForCurrency(paymentIntent.tax, 'USD') : 0,
    })
  }, [cartItems, combinedPaymentIntentData, momentsInCart, packsInCart, projectId, trackCheckoutStarted])

  const isCreditPurchase = paymentIntentData?.collectiblesCreateProductPaymentIntent?.node?.isCreditOnly

  const signature = useMemo<CollectibleCartItem | undefined>(() => {
    let sig: CollectibleCartItem | undefined
    cartItems.forEach((item) => {
      item.products?.forEach((product) => {
        if (product.__typename === 'CollectibleProductSignature') {
          sig = item
        }
      })
    })
    return sig
  }, [cartItems])

  const keepsake = useMemo<CollectibleCartItem | undefined>(() => {
    let ks: CollectibleCartItem | undefined
    cartItems.forEach((item) => {
      item.products?.forEach((product) => {
        if (product.__typename === 'CollectibleProductKeepsake') {
          ks = item
        }
      })
    })
    return ks
  }, [cartItems])

  return (
    <div className="flex flex-col rounded-lg bg-white text-black" ref={colRef}>
      <div className="mb-5 flex items-center gap-5">
        <InternalLink href={paths.collectibles.shop(projectId)} className="flex items-center gap-2">
          <ArrowLeftIcon size={18} color="core-gray-950" />
          <EyebrowSM color="core-gray-700" className="cursor-pointer">
            {t('returnToShopping', 'Return to Shopping')}
          </EyebrowSM>
        </InternalLink>
      </div>
      <TitleLG weight="bold">{t('angelCollectibleScenes', 'Angel Collectible Scenes')}</TitleLG>
      <div className="flex w-full flex-col justify-around gap-x-12 md:flex-row">
        <div className="mt-6 md:w-1/2">
          <MomentsPurchaseDescription moments={moments} />
          <PacksPurchaseDescription packs={packs} />
          {hasSignatureProductOnly && (
            <div className="grid grid-cols-[75px_1fr] grid-rows-2 gap-5">
              <img
                src={signature?.signerImage as string}
                alt="Signature Headshot"
                className="row-span-full h-auto w-full"
              />
              <TitleMD weight="semibold">Digital Collectible Signature by:</TitleMD>
              <CaptionLG>{signature?.signerName}</CaptionLG>
            </div>
          )}
          {hasKeepsakeProductOnly && (
            <div className="flex flex-col justify-start space-y-4">
              <TitleMD weight="semibold">Printed Keepsake of Your Frame</TitleMD>
              <CaptionLG>Dimension: {keepsake?.shippingSize}</CaptionLG>
              <CaptionLG>Shipping: {keepsake?.shippingTier}</CaptionLG>
            </div>
          )}
          <div className="flex flex-col gap-y-4">
            <div className="my-8 flex flex-col items-center justify-start gap-8 lg:my-20">
              <AddressSection
                isAddressState={checkoutState === 'ADDRESS'}
                changeAddressCheckoutState={changeAddressCheckoutState}
                changePaymentCheckoutState={changePaymentCheckoutState}
                handleUpdatePaymentIntentAmount={handleUpdatePaymentIntentAmount}
                orderId={paymentIntentData?.collectiblesCreateProductPaymentIntent?.node?.order?.id}
              />
            </div>
          </div>
        </div>
        <div className="my-4 flex flex-col gap-y-12 sm:gap-y-10 md:mt-0 md:w-1/3 md:rounded-xl md:bg-gray-100 md:p-10">
          <div className="flex flex-col gap-y-6">
            {hasError || canceledPaymentIntentData ? (
              <div className="flex flex-col gap-y-2 rounded-lg bg-error-500/20 p-4">
                <TitleMD weight="semibold" className="flex items-center gap-1">
                  <WarningCircleIcon size={20} />
                  {t('error', 'Error')}
                </TitleMD>
                <ParagraphSM>{t('devsNotified', 'Our team of developers has been notified.')}</ParagraphSM>
                <ParagraphSM>
                  <Translate t={t} i18nKey="contactUsForHelpV2">
                    An unexpected error occured, or your frame is no longer available. Continue shopping or contact us
                    <ExternalLink href={paths.external.zendesk.contactUs}> here</ExternalLink>.
                  </Translate>
                </ParagraphSM>
              </div>
            ) : (
              <>
                {isCreditPurchase ? (
                  <PaymentInformation
                    paymentType={t('walletCredits', 'Wallet Credits')}
                    paymentDescription={t(
                      'paymentDescriptionWithCredits',
                      'Below is a list of all taxes, fees, and available account credits. You will be charge for any remaining balance.',
                    )}
                  />
                ) : (
                  <PaymentInformation
                    paymentType={t('creditCard', 'Credit Card')}
                    paymentDescription={t(
                      'paymentDescriptionWithCreditCard',
                      'Below is a list of all taxes, and fees.',
                    )}
                  />
                )}
                <PackOrderSummary loading={false} paymentIntentData={combinedPaymentIntentData} />
              </>
            )}
            {paymentIntentData?.collectiblesCreateProductPaymentIntent?.success && (
              <PurchaseSection
                listingId={undefined}
                loading={
                  !paymentIntentCalled ||
                  paymentIntentLoading ||
                  updatedPaymentIntentLoading ||
                  checkoutState === 'ADDRESS'
                }
                isTradePurchase={false}
                isPackPurchase={false}
                isPurchaseOfferPurchase={false}
                checkoutState={checkoutState}
                combinedPaymentIntentData={combinedPaymentIntentData}
                changeAddressCheckoutState={changeAddressCheckoutState}
                projectId={projectId}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
